import {
  amazonConnectApi,
  type CommerceSyncHealth,
  type InventoryRow,
  type OrderRow,
  type SellerConnection,
} from '../../api/amazonconnect';
import { fetchOrdersMetrics, windowFromMetrics } from '../ordersTruth';
import { windowAggregate } from './aggregateOrders';
import { yesterdayWindows } from './dayWindows';
import { pctChange, trendFromPct } from './format';
import { buildFocusItems } from './focusRules';
import { buildBusinessHealth } from './healthScore';
import type { CoverageInfo, DailyBriefModel, SnapshotMetric } from './types';

const PAGE_SIZE = 100;
const MAX_ORDER_PAGES = 20;
/** Shared short TTL so Brief / KPI / Overlay / 24h pulse do not triple-scan the same page load. */
const ORDERS_SCAN_TTL_MS = 20_000;

type OrdersScan = {
  orders: OrderRow[];
  pages: number;
  total: number | null;
  truncated: boolean;
  source?: string;
};

let ordersScanCache: { connectionId: string; at: number; data: OrdersScan } | null = null;
const ordersScanInflight = new Map<string, Promise<OrdersScan>>();

export async function fetchOrdersScan(connectionId: string): Promise<OrdersScan> {
  const hit = ordersScanCache;
  if (hit && hit.connectionId === connectionId && Date.now() - hit.at < ORDERS_SCAN_TTL_MS) {
    return hit.data;
  }
  const pending = ordersScanInflight.get(connectionId);
  if (pending) return pending;

  const job = (async (): Promise<OrdersScan> => {
    const orders: OrderRow[] = [];
    let page = 1;
    let pages = 1;
    let total: number | null = null;
    let source: string | undefined;
    let truncated = false;

    while (page <= pages && page <= MAX_ORDER_PAGES) {
      const res = await amazonConnectApi.listOrdersPage(connectionId, {
        page,
        page_size: PAGE_SIZE,
        sort: 'purchase_date_desc',
        order: 'desc',
      });
      source = res.source ?? source;
      total = res.total_count ?? total;
      pages = Math.max(1, res.pages ?? 1);
      orders.push(...(res.items ?? []));
      if (!res.has_more && (res.items?.length ?? 0) < PAGE_SIZE) break;
      if (page >= MAX_ORDER_PAGES && (res.has_more || page < pages)) {
        truncated = true;
        break;
      }
      page += 1;
    }
    const data: OrdersScan = { orders, pages: page, total, truncated, source };
    ordersScanCache = { connectionId, at: Date.now(), data };
    return data;
  })().finally(() => {
    ordersScanInflight.delete(connectionId);
  });

  ordersScanInflight.set(connectionId, job);
  return job;
}

let inventoryCache: {
  connectionId: string;
  at: number;
  data: { rows: InventoryRow[]; total: number | null; zero: number };
} | null = null;
const inventoryInflight = new Map<
  string,
  Promise<{ rows: InventoryRow[]; total: number | null; zero: number }>
>();

export async function fetchInventorySample(connectionId: string): Promise<{
  rows: InventoryRow[];
  total: number | null;
  zero: number;
}> {
  const hit = inventoryCache;
  if (hit && hit.connectionId === connectionId && Date.now() - hit.at < ORDERS_SCAN_TTL_MS) {
    return hit.data;
  }
  const pending = inventoryInflight.get(connectionId);
  if (pending) return pending;
  const job = (async () => {
    const res = await amazonConnectApi.listInventoryPage(connectionId, {
      page: 1,
      page_size: PAGE_SIZE,
      sort: 'available',
      order: 'asc',
    });
    const rows = res.items ?? [];
    const zero = rows.filter((r) => (r.available ?? 0) <= 0 && (r.fba_inventory ?? 0) <= 0).length;
    const data = { rows, total: res.total_count ?? null, zero };
    inventoryCache = { connectionId, at: Date.now(), data };
    return data;
  })().finally(() => {
    inventoryInflight.delete(connectionId);
  });
  inventoryInflight.set(connectionId, job);
  return job;
}

function greetName(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function staleHours(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return (Date.now() - t) / 3_600_000;
}

function ordersDeepLink(connectionId: string, from: string, to: string, extra = ''): string {
  const base = `/amazon/orders?connection=${encodeURIComponent(connectionId)}&from=${from}&to=${to}`;
  return extra ? `${base}${extra.startsWith('&') ? extra : `&${extra}`}` : base;
}

export async function loadDailyBrief(opts: {
  connection: SellerConnection | null;
  health: CommerceSyncHealth | null;
}): Promise<DailyBriefModel> {
  const { yesterday, prior } = yesterdayWindows();
  const connection = opts.connection;
  const connectionId = connection?.id ?? null;

  if (!connectionId) {
    return {
      loadedAt: new Date().toISOString(),
      connectionId: null,
      sellerRef: '—',
      connectionStatus: 'unknown',
      marketplaceIds: [],
      greetingName: greetName(),
      yesterdayLabel: yesterday.label,
      priorLabel: prior.label,
      metrics: emptyMetrics(),
      focus: [
        {
          id: 'no-connection',
          severity: 'critical',
          title: 'No Amazon connection selected',
          evidence: 'Open Connect to authorize a seller account.',
          href: '/amazon/connect',
        },
      ],
      health: {
        overall: null,
        band: 'unavailable',
        componentsReady: 0,
        componentsTotal: 6,
        components: [],
        confidence: 0,
        confidenceLabel: 'low',
      },
      lastSuccessfulSync: null,
      coverage: {
        ordersTotalInStore: null,
        ordersScanned: 0,
        ordersPagesFetched: 0,
        ordersTruncated: false,
        inventorySampled: 0,
        inventoryTotal: null,
        domains: [],
      },
      error: null,
      sourceNote: 'Certified LIVE domains only. No data until a connection is available.',
    };
  }

  const ordersRow = opts.health?.domains?.find(
    (d) => d.marketsync_domain === 'orders' || d.key === 'orders',
  );
  const ordersHref = ordersDeepLink(connectionId, yesterday.isoFrom, yesterday.isoTo);
  const inventoryHref = `/amazon/inventory?connection=${encodeURIComponent(connectionId)}`;
  const connectHref = `/amazon/connect?connection=${encodeURIComponent(connectionId)}`;

  let ordersError: string | undefined;
  let inv = { rows: [] as InventoryRow[], total: null as number | null, zero: 0 };
  let metricsIncomplete = false;
  let metricsReason: string | undefined;
  let yAgg = windowAggregate([], yesterday);
  let pAgg = windowAggregate([], prior);

  try {
    const [yMet, pMet, i] = await Promise.all([
      fetchOrdersMetrics(connectionId, {
        from: yesterday.isoFrom,
        to: yesterday.isoTo,
        tz: 'Asia/Kolkata',
      }),
      fetchOrdersMetrics(connectionId, {
        from: prior.isoFrom,
        to: prior.isoTo,
        tz: 'Asia/Kolkata',
      }),
      fetchInventorySample(connectionId).catch(() => ({
        rows: [] as InventoryRow[],
        total: null,
        zero: 0,
      })),
    ]);
    inv = i;
    metricsIncomplete = !yMet.complete || !pMet.complete;
    metricsReason = yMet.incomplete_reason || pMet.incomplete_reason;
    yAgg = windowFromMetrics(yMet);
    pAgg = windowFromMetrics(pMet);
  } catch (e) {
    ordersError = e instanceof Error ? e.message : 'Failed to load orders metrics';
  }

  if (ordersError) {
    return {
      loadedAt: new Date().toISOString(),
      connectionId,
      sellerRef: connection.seller_ref || connection.id,
      connectionStatus: connection.status,
      marketplaceIds: connection.marketplace_ids ?? [],
      greetingName: greetName(),
      yesterdayLabel: yesterday.label,
      priorLabel: prior.label,
      metrics: emptyMetrics().map((m) => ({
        ...m,
        href: ordersHref,
        reason: ordersError,
      })),
      focus: [
        {
          id: 'metrics-error',
          severity: 'critical',
          title: 'Gate A metrics unavailable',
          evidence: ordersError,
          href: ordersHref,
        },
      ],
      health: {
        overall: null,
        band: 'unavailable',
        componentsReady: 0,
        componentsTotal: 6,
        components: [],
        confidence: 0,
        confidenceLabel: 'low',
      },
      lastSuccessfulSync: ordersRow?.last_sync_at || connection.last_sync_at || null,
      coverage: {
        ordersTotalInStore: null,
        ordersScanned: 0,
        ordersPagesFetched: 0,
        ordersTruncated: false,
        inventorySampled: 0,
        inventoryTotal: null,
        domains: [],
      },
      error: ordersError,
      sourceNote: 'Gate A Metrics unavailable — no quiet zero sales.',
    };
  }

  const lastSync = connection.last_sync_at || ordersRow?.last_sync_at || null;
  let stale = staleHours(lastSync);
  if (!metricsIncomplete && !ordersError) {
    stale = Math.min(stale ?? 4, 4);
  }

  const metrics = buildMetrics({
    connectionId,
    y: yAgg,
    p: pAgg,
    yFrom: yesterday.isoFrom,
    yTo: yesterday.isoTo,
    incomplete: metricsIncomplete,
    incompleteReason: metricsReason,
  });

  const focus = buildFocusItems({
    connectionId,
    connectionStatus: connection.status,
    yesterday: yAgg,
    prior: pAgg,
    inventoryZero: inv.rows.length ? inv.zero : null,
    inventorySample: inv.rows.length,
    inventoryTotal: inv.total,
    ordersDomainQuality: ordersRow?.quality,
    ordersLastSync: lastSync ?? undefined,
    ordersError,
    syncStaleHours: stale,
    ordersScanTruncated: metricsIncomplete,
    ordersHrefBase: ordersHref,
    inventoryHrefBase: inventoryHref,
    connectHref,
  });

  const health = buildBusinessHealth({
    yesterday: yAgg,
    prior: pAgg,
    inventoryZero: inv.rows.length ? inv.zero : null,
    inventorySample: inv.rows.length,
    connectionStatus: connection.status,
    staleHours: stale,
    ordersQuality: ordersRow?.quality,
    hrefs: { orders: ordersHref, inventory: inventoryHref, connect: connectHref },
  });

  if (metricsIncomplete) {
    health.confidence = Math.max(0, health.confidence - 15);
    health.confidenceLabel =
      health.confidence >= 70 ? 'high' : health.confidence >= 40 ? 'medium' : 'low';
  }

  const coverage: CoverageInfo = {
    ordersTotalInStore: yAgg.orderCount,
    ordersScanned: yAgg.orderCount,
    ordersPagesFetched: 0,
    ordersTruncated: metricsIncomplete,
    inventorySampled: inv.rows.length,
    inventoryTotal: inv.total,
    domains: (opts.health?.domains ?? [])
      .filter((d) =>
        ['orders', 'order_items', 'inventory', 'finance', 'catalog', 'pricing', 'returns', 'fba_inventory', 'sales_traffic', 'settlement', 'shipments'].includes(
          d.marketsync_domain || d.key,
        ),
      )
      .map((d) => ({
        key: d.marketsync_domain || d.key,
        quality: d.quality,
        lastSync: d.last_sync_at,
        records: d.records_persisted || d.records_received,
      })),
  };

  return {
    loadedAt: new Date().toISOString(),
    connectionId,
    sellerRef: connection.seller_ref || connection.id,
    connectionStatus: connection.status,
    marketplaceIds: connection.marketplace_ids ?? [],
    greetingName: greetName(),
    yesterdayLabel: yesterday.label,
    priorLabel: prior.label,
    metrics,
    focus,
    health,
    lastSuccessfulSync: lastSync,
    coverage,
    error: null,
    sourceNote: metricsIncomplete
      ? `Gate A Metrics incomplete (${metricsReason ?? 'coverage'}). Sales=header SUM; Order items=line COUNT; Units=SUM(quantity_ordered).`
      : 'Gate A Metrics: Sales=header SUM; Order items=line COUNT; Units=SUM(quantity_ordered). IST window.',
  };
}

function emptyMetrics(): SnapshotMetric[] {
  return (['sales', 'orders', 'units', 'aov'] as const).map((id) => ({
    id,
    label:
      id === 'sales'
        ? 'Sales'
        : id === 'orders'
          ? 'Order items'
          : id === 'units'
            ? 'Units'
            : 'Average order value',
    availability: 'unavailable',
    value: null,
    currencies: [],
    coverage: 0,
    prior: null,
    pctChange: null,
    trend: 'na',
    href: '/amazon/connect',
    reason: 'No connection',
  }));
}

function buildMetrics(opts: {
  connectionId: string;
  y: ReturnType<typeof windowAggregate>;
  p: ReturnType<typeof windowAggregate>;
  yFrom: string;
  yTo: string;
  incomplete?: boolean;
  incompleteReason?: string;
}): SnapshotMetric[] {
  const href = ordersDeepLink(opts.connectionId, opts.yFrom, opts.yTo);
  const y = opts.y;
  const p = opts.p;
  const partial = opts.incomplete ? 'partial' : 'available';

  const salesPct = pctChange(y.sales, p.sales);
  const ordersPct = pctChange(y.orderCount, p.orderCount);
  const unitsPct = pctChange(y.units, p.units);
  const aovPct = pctChange(y.aov, p.aov);

  return [
    {
      id: 'sales',
      label: 'Sales',
      availability: (y.sales == null ? 'unavailable' : partial) as SnapshotMetric['availability'],
      value: y.sales,
      currencies: y.currencies,
      coverage: opts.incomplete ? 0.5 : 1,
      prior: p.sales,
      pctChange: salesPct,
      trend: trendFromPct(salesPct),
      href,
      reason: opts.incomplete ? opts.incompleteReason : undefined,
    },
    {
      id: 'orders',
      label: 'Order items',
      availability: partial as SnapshotMetric['availability'],
      value: y.orderCount,
      currencies: [],
      coverage: opts.incomplete ? 0.5 : 1,
      prior: p.orderCount,
      pctChange: ordersPct,
      trend: trendFromPct(ordersPct),
      href,
      reason: opts.incomplete ? opts.incompleteReason : undefined,
    },
    {
      id: 'units',
      label: 'Units',
      availability: (y.units == null ? 'unavailable' : partial) as SnapshotMetric['availability'],
      value: y.units,
      currencies: [],
      coverage: opts.incomplete ? 0.5 : 1,
      prior: p.units,
      pctChange: unitsPct,
      trend: trendFromPct(unitsPct),
      href,
      reason: opts.incomplete
        ? opts.incompleteReason
        : 'SUM(quantity_ordered) on order_items lines',
    },
    {
      id: 'aov',
      label: 'Average order value',
      availability: (y.aov == null ? 'unavailable' : partial) as SnapshotMetric['availability'],
      value: y.aov,
      currencies: y.currencies,
      coverage: opts.incomplete ? 0.5 : 1,
      prior: p.aov,
      pctChange: aovPct,
      trend: trendFromPct(aovPct),
      href,
      reason: opts.incomplete ? opts.incompleteReason : 'Sales / Order items',
    },
  ];
}
